import { useId, useState } from 'react';
import { Alert } from './Alert.js';
import { Button } from './Button.js';
import { Icon } from './Icon.js';
import { Select } from './Select.js';
import {
  addStageEvent,
  removeStageEvent,
  type StageEvent,
  type StageEventsConfig,
} from './stageEvents.js';

/**
 * O editor dos eventos de UMA fase: a lista do que acontece quando um cartão
 * entra nela, e a linha para acrescentar mais um — tipo e destino, os dois
 * obrigatórios.
 *
 * É da fase, e não do quadro: o `Kanban` o usa no formulário de fase, e
 * qualquer tela que configure fases (a lista de fases de uma visão em tabela,
 * por exemplo) usa o mesmo. Nada aqui é gravado sozinho — a lista vai junto
 * com o resto da fase no "Salvar" de quem o monta.
 */
export function StageEventsEditor({
  config,
  events,
  onChange,
}: {
  config: StageEventsConfig;
  events: StageEvent[];
  onChange: (events: StageEvent[]) => void;
}) {
  const [type, setType] = useState('');
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const heading = useId();

  const typeLabel = (t: string) => config.catalog.find((c) => c.type === t)?.label ?? t;
  const targetName = (id: string) => config.targets.find((t) => t.id === id)?.label ?? id;
  const unavailable =
    config.catalog.length === 0
      ? 'Nenhum tipo de evento disponível ainda.'
      : config.targets.length === 0
        ? `Nenhuma opção de ${config.targetLabel.toLocaleLowerCase('pt-BR')} disponível.`
        : null;

  const add = () => {
    const next = addStageEvent(events, { id: crypto.randomUUID(), type, targetId });
    if (!next) {
      return setError(`A fase já tem “${typeLabel(type)}” para ${targetName(targetId)}.`);
    }
    setError(null);
    setType('');
    setTargetId('');
    onChange(next);
  };

  return (
    <section
      aria-labelledby={heading}
      className="flex flex-col gap-3 border-t border-borda-suave pt-4"
    >
      <div>
        <h3 id={heading} className="text-sm font-semibold">
          Eventos ao entrar nesta fase
        </h3>
        <p className="mt-0.5 text-xs text-texto-suave">
          Cada evento escolhe o próprio destino. Quem move o cartão sem poder usar o
          destino deixa o evento esperando a autorização de quem pode.
        </p>
      </div>

      {config.notice && <Alert tone="informacao">{config.notice}</Alert>}

      {events.length === 0 ? (
        <p className="text-sm text-texto-suave">
          Nenhum evento: o cartão só muda de fase.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda-suave rounded-borda border border-borda">
          {events.map((e) => (
            <li key={e.id} className="flex items-center gap-3 py-1.5 pr-1.5 pl-3.5">
              <Icon nome="tomada" size={16} className="shrink-0 text-texto-suave" />
              <span className="min-w-0 flex-1 text-sm">
                <span className="font-medium">{typeLabel(e.type)}</span>
                <span className="text-texto-suave"> · {targetName(e.targetId)}</span>
              </span>
              <Button
                type="button"
                variant="discreto"
                size="sm"
                onClick={() => onChange(removeStageEvent(events, e.id))}
                aria-label={`Remover ${typeLabel(e.type)} em ${targetName(e.targetId)}`}
              >
                Remover
              </Button>
            </li>
          ))}
        </ul>
      )}

      {unavailable ? (
        <p className="text-sm text-texto-suave">{unavailable}</p>
      ) : (
        <div className="@container">
          <div className="grid gap-3 @lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] @lg:items-end">
            <Select
              label="Evento"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setError(null);
              }}
            >
              <option value="">Escolha…</option>
              {config.catalog.map((c) => (
                <option key={c.type} value={c.type}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Select
              label={config.targetLabel}
              value={targetId}
              onChange={(e) => {
                setTargetId(e.target.value);
                setError(null);
              }}
            >
              <option value="">Escolha…</option>
              {config.targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              variant="contorno"
              icon="mais"
              disabled={!type || !targetId}
              onClick={add}
            >
              Adicionar evento
            </Button>
          </div>
          {/* Fora da grade, e não no `error` do `Select`: a linha da grade
              alinha os campos e o botão pelo pé, e o erro embaixo de um campo
              só desceria aquele campo. */}
          {error && (
            <p role="alert" className="mt-1.5 text-xs font-medium text-erro">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
